import {
	call,
	put,
	takeLatest,
	takeEvery,
	all,
	retry,
	CallEffect,
	delay,
	select,
	fork,
} from "redux-saga/effects";

import {
	orderQuoteRequested,
	orderQuoteSuccess,
	orderQuoteError,
	markOrderCompleteRequested,
	markOrderCompleteSuccess,
	markOrderCompleteError,
	orderItemUpdateSuccess,
	orderItemUpdateError,
	orderItemUpdateRequested,
} from "../slices/order.slice";
import {
	changeUploadOnGoingState,
	documentUploadUpdate,
	incrementSNo,
	s3BucketInfoRequested,
} from "../slices/upload.slice";
import {
	createOrderQuote,
	refreshToken,
	updateOrderAsDone,
	updateOrderItems,
} from "../../services/api.services";
import { logClientError } from "../../services/client-logger.service";
import { RootState, store } from "../root";
import {
	DriveFileUpload,
	FileUploadStatus,
	UploadStatus,
} from "../types/upload.types";
import { PayloadAction } from "@reduxjs/toolkit";
import { QuoteOrderPayload } from "../types/order.types";
import { setStorredUserData } from "../slices/common.slice";
import { startLoginRequest, verifyOtpSuccess } from "../slices/auth.slice";
import { RootOptions } from "react-dom/client";
import { toast } from "sonner";

function* checkAllFilesAreUploadedOrNot(): Generator<any, boolean, any> {
	const { Total, Completed, Failed, UnSupportedFile } = yield select(
		(state: RootState) => state.upload.progress
	);

	return Total === Completed + Failed + UnSupportedFile;
}

const waitForInternet = () =>
	new Promise((resolve) => {
		const stabilizeAndResolve = () => {
			console.log(
				"🔌 Network connection detected. Waiting 5 seconds for OS to stabilize internet access..."
			);
			setTimeout(() => resolve(true), 5000); // 5-second mandatory buffer
		};

		if (navigator.onLine) {
			stabilizeAndResolve();
		} else {
			window.addEventListener("online", stabilizeAndResolve, { once: true });
		}
	});

function* retryWithDelays<T>(
	apiFn: (payload: T) => Promise<any>,
	payload: T,
	delays: number[]
): Generator<any, any, any> {
	let lastError;

	for (let i = 0; i <= delays.length; i++) {
		try {
			return yield call(apiFn, payload);
		} catch (error) {
			lastError = error;

			// THE SMART PAUSE: Check if the internet is down before burning a retry
			if (!navigator.onLine) {
				console.log("🔌 Internet disconnected during API save! Pausing...");

				toast.error(
					"Your network connection is offline. Uploads will wait and resume automatically once you're back online.",
					{ id: "api-offline-toast", duration: 10000 }
				);

				// Freeze the generator until the connection is restored
				yield call(waitForInternet);

				toast.success("Network restored! Finalizing file...", {
					id: "api-offline-toast",
					duration: 4000,
				});

				console.log("📡 Internet restored! Resuming API call...");

				i--;

				// Immediately restart the loop to try the API call again
				continue;
			}

			// If we reach here, the user is online but the server actually failed.
			// Check if we have exhausted all our allowed retries.
			if (i === delays.length) {
				throw lastError;
			}

			// Standard backoff for micro-drops or server hiccups
			console.warn(`API call failed. Retrying in ${delays[i]}ms...`);
			yield delay(delays[i]);
		}
	}
}
/* ------------------------------ WORKERS ------------------------------- */

function* orderQuoteWorker(action: PayloadAction<QuoteOrderPayload>): any {
	const { recaptchaToken } = yield select((state: RootState) => state.auth);
	try {
		const res = yield retry(3, 1000, createOrderQuote, action.payload);

		yield put(orderQuoteSuccess(res.data.data.orderId));
		yield put(s3BucketInfoRequested());
	} catch (err: any) {
		yield call(logClientError, {
			level: "error",
			event: "order_quote_failed",
			message: "Failed to create order quote",
			error: err,
			context: {
				statusCode: err?.response?.status,
				errorMessage: err?.message,
			},
		});
		yield put(orderQuoteError(err.message));
	}
}

function* updateOrderItem(
	action: PayloadAction<{ file: DriveFileUpload }>
): Generator<any, void, any> {
	const file = action.payload.file;

	try {
		const { orderId } = yield select((state: RootState) => state.order);

		yield put(incrementSNo());

		const { fileSnoCounter, s3FolderName } = store.getState().upload;
		const fileData = {
			orderId: orderId,
			fileName: file.name,
			fileSize: file?.size > 0 ? file?.size : 1,
			uploadType: 1,
			fileSno: fileSnoCounter,
			fileStatus:
				file.status === FileUploadStatus.UnsupportedFileType
					? UploadStatus.unsupportedFile
					: file.status === FileUploadStatus.Failed
						? UploadStatus.uploadFailed
						: file.status === FileUploadStatus.UploadComplete
							? UploadStatus.uploadSuccess
							: UploadStatus.uploadFailed,
			orderActivityId: 1,
			createBy: 1,
			refOrderItemType: 1,
			filePath: `${s3FolderName}/${orderId}/${file.id}.${file.name?.split(".").reverse()[0]}`,
			folderName: orderId,
			relativeFolderPath:
				file?.path?.length && file?.path?.length > 0
					? `${orderId}${file.path}`.split("/").slice(0, -1).join("/")
					: `${orderId}`,
		};

		const res = yield* retryWithDelays(
			updateOrderItems,
			{ orderItems: [fileData] },
			[1000, 3000, 5000, 10000]
		);

		yield put(orderItemUpdateSuccess());

		yield put(
			documentUploadUpdate({
				id: file.id,
				changes: {
					status:
						file.status === FileUploadStatus.UploadComplete
							? FileUploadStatus.Completed
							: file.status,
				},
			})
		);
	} catch (error) {
		console.error("Error updating order item:", error);

		yield fork(logClientError, {
			level: "error",
			event: "order_item_update_failed",
			message: "Failed to update order item status",
			error,
			context: {
				fileId: file.id,
				fileName: file.name,
			},
		});

		yield put(orderItemUpdateError(error));
		yield put(
			documentUploadUpdate({
				id: file.id,
				changes: {
					status: FileUploadStatus.Failed,
				},
			})
		);
	} finally {
		const allUploaded: boolean = yield call(checkAllFilesAreUploadedOrNot);
		console.log("All files uploaded check:", allUploaded);

		if (allUploaded) {
			yield call(markOrderCompleteWorker);
		}
	}
}

function* markOrderCompleteWorker(): any {
	const {
		progress: { Completed },
		uploadedFileSize,
	} = yield select((state: RootState) => state.upload);
	const { orderId } = store.getState().order;

	const uploadFormData = store.getState().uploadForm;

	try {
		const res = yield retry(3, 1000, updateOrderAsDone, {
			orderId,
			fileSize: uploadedFileSize > 0 ? uploadedFileSize : 1,
			totalCount: Completed,
			zipName: `${orderId}.zip`,
		});
		yield put(markOrderCompleteSuccess(res.data));

		yield put(setStorredUserData(uploadFormData));
	} catch (err: any) {
		yield call(logClientError, {
			level: "error",
			event: "mark_order_complete_failed",
			message: "Failed to mark order as complete",
			error: err,
			context: {
				orderId,
				uploadedFileSize,
				completedFiles: Completed,
			},
		});
		yield put(markOrderCompleteError(err.message));
		yield put(changeUploadOnGoingState(false));
	}
}

/* ------------------------------- ROOT SAGA ---------------------------- */

export function* orderSaga() {
	yield all([
		takeLatest(orderQuoteRequested.type, orderQuoteWorker),
		takeLatest(markOrderCompleteRequested.type, markOrderCompleteWorker),
		takeEvery(orderItemUpdateRequested.type, updateOrderItem),
	]);
}
